import React, { useState } from 'react';
import EnterpriseColorPalette from '../components/ui/EnterpriseColorPalette';

const DesignSystem: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'components'>('colors');

  return (
    <div className="min-h-screen bg-secondary-50">
      <header className="bg-aws-blue text-white py-4">
        <div className="qnav-container">
          <h1 className="text-2xl font-bold">Navigator Chat Design System</h1>
        </div>
      </header>

      <div className="qnav-container py-6">
        <div className="bg-surface rounded-qnav shadow-brand-card border border-secondary-200 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-secondary-200">
            <button
              className={`px-6 py-3 font-medium ${
                activeTab === 'colors'
                  ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-500'
                  : 'text-secondary-600 hover:bg-secondary-50'
              }`}
              onClick={() => setActiveTab('colors')}
            >
              Colors
            </button>
            <button
              className={`px-6 py-3 font-medium ${
                activeTab === 'typography'
                  ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-500'
                  : 'text-secondary-600 hover:bg-secondary-50'
              }`}
              onClick={() => setActiveTab('typography')}
            >
              Typography
            </button>
            <button
              className={`px-6 py-3 font-medium ${
                activeTab === 'components'
                  ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-500'
                  : 'text-secondary-600 hover:bg-secondary-50'
              }`}
              onClick={() => setActiveTab('components')}
            >
              Components
            </button>
          </div>

          {/* Content */}
          <div>
            {activeTab === 'colors' && <EnterpriseColorPalette />}

            {activeTab === 'typography' && (
              <div className="p-6">
                <h1 className="qnav-heading-1 mb-6">Typography System</h1>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Headings</h2>
                  <div className="space-y-6">
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h1 className="qnav-heading-1">Heading 1</h1>
                      <p className="text-xs text-secondary-500 mt-2">32px / 40px, font-bold, text-secondary-900</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h2 className="qnav-heading-2">Heading 2</h2>
                      <p className="text-xs text-secondary-500 mt-2">24px / 32px, font-semibold, text-secondary-800</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h3 className="qnav-heading-3">Heading 3</h3>
                      <p className="text-xs text-secondary-500 mt-2">20px / 28px, font-medium, text-secondary-800</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h4 className="qnav-heading-4">Heading 4</h4>
                      <p className="text-xs text-secondary-500 mt-2">18px / 24px, font-medium, text-secondary-800</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Body Text</h2>
                  <div className="space-y-6">
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="qnav-body-lg">Large Body Text - The quick brown fox jumps over the lazy dog.</p>
                      <p className="text-xs text-secondary-500 mt-2">18px / 28px, font-normal, text-secondary-700</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="qnav-body">Default Body Text - The quick brown fox jumps over the lazy dog.</p>
                      <p className="text-xs text-secondary-500 mt-2">16px / 24px, font-normal, text-secondary-700</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="qnav-body-sm">Small Body Text - The quick brown fox jumps over the lazy dog.</p>
                      <p className="text-xs text-secondary-500 mt-2">14px / 20px, font-normal, text-secondary-700</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="qnav-body-xs">
                        Extra Small Body Text - The quick brown fox jumps over the lazy dog.
                      </p>
                      <p className="text-xs text-secondary-500 mt-2">12px / 16px, font-normal, text-secondary-600</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Font Weights</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="text-lg font-bold">Bold (700)</p>
                      <p className="text-xs text-secondary-500 mt-2">Used for main headings and emphasis</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="text-lg font-semibold">Semibold (600)</p>
                      <p className="text-xs text-secondary-500 mt-2">Used for subheadings and important UI elements</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="text-lg font-medium">Medium (500)</p>
                      <p className="text-xs text-secondary-500 mt-2">Used for buttons, labels, and minor headings</p>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <p className="text-lg font-normal">Regular (400)</p>
                      <p className="text-xs text-secondary-500 mt-2">Used for body text and general content</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'components' && (
              <div className="p-6">
                <h1 className="qnav-heading-1 mb-6">UI Components</h1>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Buttons</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h3 className="font-medium mb-3">Primary Buttons</h3>
                      <div className="space-y-2">
                        <button className="qnav-btn-primary w-full">Primary Button</button>
                        <button className="qnav-btn-primary w-full" disabled>
                          Disabled
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h3 className="font-medium mb-3">Secondary Buttons</h3>
                      <div className="space-y-2">
                        <button className="qnav-btn-secondary w-full">Secondary Button</button>
                        <button className="qnav-btn-secondary w-full" disabled>
                          Disabled
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h3 className="font-medium mb-3">Outline Buttons</h3>
                      <div className="space-y-2">
                        <button className="qnav-btn-outline w-full">Outline Button</button>
                        <button className="qnav-btn-outline w-full" disabled>
                          Disabled
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Cards</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="qnav-card">
                      <div className="qnav-card-header">Standard Card</div>
                      <div className="qnav-card-body">
                        <p>This is a standard card component with header and body sections.</p>
                      </div>
                    </div>
                    <div className="qnav-card-accent">
                      <div className="qnav-card-header">Accent Card</div>
                      <div className="qnav-card-body">
                        <p>This is an accent card component with header and body sections.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Form Elements</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h3 className="font-medium mb-3">Text Inputs</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="qnav-label">Standard Input</label>
                          <input type="text" className="qnav-input" placeholder="Enter text..." />
                        </div>
                        <div>
                          <label className="qnav-label">Disabled Input</label>
                          <input type="text" className="qnav-input" placeholder="Disabled input" disabled />
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-surface border border-secondary-200 rounded-md">
                      <h3 className="font-medium mb-3">Selection Controls</h3>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input type="checkbox" id="checkbox1" className="qnav-checkbox" />
                          <label htmlFor="checkbox1" className="ml-2">
                            Checkbox
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input type="radio" id="radio1" name="radio-group" className="qnav-radio" />
                          <label htmlFor="radio1" className="ml-2">
                            Radio Option 1
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input type="radio" id="radio2" name="radio-group" className="qnav-radio" />
                          <label htmlFor="radio2" className="ml-2">
                            Radio Option 2
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="qnav-heading-2 mb-4">Alerts & Notifications</h2>
                  <div className="space-y-4">
                    <div className="qnav-alert-success">
                      <strong>Success!</strong> Your changes have been saved successfully.
                    </div>
                    <div className="qnav-alert-info">
                      <strong>Information:</strong> This is an informational message.
                    </div>
                    <div className="qnav-alert-warning">
                      <strong>Warning:</strong> Please review your inputs before proceeding.
                    </div>
                    <div className="qnav-alert-error">
                      <strong>Error:</strong> There was a problem processing your request.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignSystem;
